import uuid from 'uuid-random';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslations } from 'js/hooks/i18n';
import { validateGeolocation } from 'js/hooks/auth/pas';
import { useSmartClosePopups } from 'js/components/common/PopupManager';

import { useXpointDownloadVerifyApp } from './useXpointDownloadVerifyApp';
import { showGeofencingPopupAction } from '../../../actions';
import * as Types from '../types';
import * as helpers from '../helpers';

export const XPOINT_ERROR_POPUP_ID = uuid();

export const useXpointConnect = ({
    sdk,
    onConnect,
    useTransparentPopup,
    setShowOverlay
}: {
    sdk: Types.XPointSDKInstance | null;
    onConnect: CallableFunction;
    useTransparentPopup: boolean;
    setShowOverlay: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const downloadVerifyApp = useXpointDownloadVerifyApp();
    const closePopups = useSmartClosePopups();
    const dispatch = useDispatch();
    const t = useTranslations();

    return useCallback(async function checkConnect() {
        if (!sdk) {
            return;
        }

        if (!await sdk.isVerifyActive!()) {
            await helpers.openURL();
        }

        if (!await sdk.isVerifyActive!()) {
            dispatch(showGeofencingPopupAction({
                id: XPOINT_ERROR_POPUP_ID,
                title: 'playtech.mobile.system.xpoint.popups.error.title',
                content: t('playtech.mobile.system.xpoint.popups.error.-1'),
                isModal: true,
                buttons: [
                    {
                        onClick: downloadVerifyApp,
                        text: 'playtech.mobile.system.xpoint.popups.installer.download'
                    },
                    {
                        onClick: () => {
                            closePopups(XPOINT_ERROR_POPUP_ID);
                            setShowOverlay(false);
                        },
                        text: 'playtech.mobile.system.xpoint.popups.installer.ignore',
                        color: 'negative'
                    },
                    {
                        onClick: () => {
                            closePopups(XPOINT_ERROR_POPUP_ID);
                            checkConnect();
                        },
                        text: 'playtech.mobile.system.xpoint.popups.installer.refresh',
                        color: 'info'
                    }
                ],
                useTransparentPopup
            }));

            try {
                const response = await validateGeolocation(null, null, null, null, null, 'xpoint');
                const sessionValidationData = response.data.sessionValidationData || {};
                const sessionValidationByGeolocationData = sessionValidationData.SessionValidationByGeolocationData || [];
                const geolocationData = sessionValidationByGeolocationData[0] || {};

                await validateGeolocation(null, null, null, geolocationData.transactionId, String(-1), 'xpoint');
            } catch (e) {
                // noop
            }

            return;
        }

        onConnect();
    }, [
        t,
        sdk,
        closePopups,
        dispatch,
        onConnect,
        setShowOverlay,
        useTransparentPopup,
        downloadVerifyApp
    ]);
};
